/**
 * Marco de la aplicacion: menu lateral, contenido a pantalla completa y el
 * titulo de la pestana.
 *
 * El menu muestra siempre el ambiente y quien esta conectado: en una
 * herramienta con reproceso manual, saber si estas mirando produccion o local no
 * puede depender de la memoria. Por lo mismo la pestana lleva la seccion y el
 * ambiente en el titulo: con varias abiertas, se encuentran por su nombre.
 *
 * Tres formas, todas con el mismo arbol (el CSS decide, ver global.css):
 * - menu expandido, con textos;
 * - riel de iconos, si el usuario lo contrae (se recuerda en `middle:sidebar`),
 *   donde cada item conserva su nombre accesible y lo muestra como tooltip;
 * - en pantallas angostas (< 900 px), un panel que se abre desde una barra
 *   superior compacta y se cierra al navegar, con Escape o tocando fuera.
 */
import { useEffect, useRef, useState } from 'react';
import type { ComponentType, MouseEvent, ReactElement } from 'react';
import { Link, Outlet, matchPath, useLocation } from 'react-router-dom';
import type { SimulationCapabilities } from '../api/contracts';
import type { SessionUser } from '../api/types';
import { useMonitorApi } from '../app/api-context';
import { readEnvironment } from '../app/environment';
import { readLocalPreference, writeLocalPreference } from '../app/local-preferences';
import { useAuth } from '../auth/auth-context';
import { ROLE_LABELS } from '../auth/roles';
import { useAsyncResource } from '../hooks/use-async-resource';
import { useDocumentTitle } from '../hooks/use-document-title';
import { classifyEnvironment } from '../lib/environment';
import type { EnvironmentKind } from '../lib/environment';
import { EnvironmentBadge } from './EnvironmentBadge';
import {
  IconClose,
  IconDashboard,
  IconDeadLetter,
  IconLogout,
  IconMenu,
  IconMessages,
  IconProcesses,
  IconQuarantine,
  IconRuns,
  IconSidebarCollapse,
  IconSidebarExpand,
  IconWarehouse,
} from './icons';
import type { IconProps } from './icons';
import { MiddleMark } from './MiddleLogo';
import { ThemeCycleButton, ThemeSwitcher } from './ThemeSwitcher';

const SIDEBAR_STORAGE_KEY = 'middle:sidebar';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ComponentType<IconProps>;
  /** Rutas bajo las que el item queda marcado como la seccion actual. */
  readonly sections: readonly string[];
}

interface NavGroup {
  readonly id: string;
  readonly title: string;
  readonly items: readonly NavItem[];
  /**
   * Solo si la API dice que la simulacion esta encendida en un ambiente que no
   * es produccion (`simulationOffered`). La simulacion escribe de verdad en
   * Janis QA; el menu es una cortesia y las barreras siguen en la API.
   */
  readonly requiresSimulation?: boolean;
}

const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'operacion',
    title: 'Operación',
    items: [
      { to: '/tablero', label: 'Tablero', icon: IconDashboard, sections: ['/tablero'] },
      // El flujo de una corrida se abre desde Corridas y ahi se vuelve; el
      // detalle y la traza cuelgan de un mensaje.
      {
        to: '/corridas',
        label: 'Corridas',
        icon: IconRuns,
        sections: ['/corridas', '/flujo'],
      },
      {
        to: '/mensajes',
        label: 'Mensajes',
        icon: IconMessages,
        sections: ['/mensajes', '/traza'],
      },
    ],
  },
  {
    id: 'incidentes',
    title: 'Incidentes',
    items: [
      {
        to: '/dead-letters',
        label: 'Dead letters',
        icon: IconDeadLetter,
        sections: ['/dead-letters'],
      },
      { to: '/cuarentena', label: 'Cuarentena', icon: IconQuarantine, sections: ['/cuarentena'] },
    ],
  },
  {
    id: 'configuracion',
    title: 'Configuración',
    items: [
      { to: '/facturador', label: 'Facturador', icon: IconWarehouse, sections: ['/facturador'] },
      { to: '/bodegas', label: 'Bodegas', icon: IconWarehouse, sections: ['/bodegas'] },
      {
        to: '/entregas-sap',
        label: 'Entregas SAP',
        icon: IconWarehouse,
        sections: ['/entregas-sap'],
      },
      {
        to: '/webhooks-salientes',
        label: 'Webhooks salientes',
        icon: IconProcesses,
        sections: ['/webhooks-salientes'],
      },
    ],
  },
  {
    id: 'simulacion',
    title: 'Simulación',
    requiresSimulation: true,
    items: [
      {
        to: '/simulacion/picking',
        label: 'Picking del WMS',
        icon: IconWarehouse,
        sections: ['/simulacion'],
      },
    ],
  },
  {
    id: 'referencia',
    title: 'Referencia',
    items: [
      { to: '/procesos', label: 'Procesos', icon: IconProcesses, sections: ['/procesos'] },
      {
        to: '/referencia/webhooks-salientes',
        label: 'Guía de webhooks',
        icon: IconProcesses,
        sections: ['/referencia/webhooks-salientes'],
      },
    ],
  },
];

/** Nombre de cada pantalla en la pestana. */
const PAGE_TITLES: readonly { readonly path: string; readonly title: string }[] = [
  { path: '/', title: 'Tablero' },
  { path: '/tablero', title: 'Tablero' },
  { path: '/mensajes', title: 'Mensajes' },
  { path: '/mensajes/:id', title: 'Mensaje' },
  { path: '/traza/:correlationId', title: 'Traza' },
  { path: '/flujo/:correlationId', title: 'Flujo' },
  { path: '/corridas', title: 'Corridas' },
  { path: '/dead-letters', title: 'Dead letters' },
  { path: '/cuarentena', title: 'Cuarentena' },
  { path: '/facturador', title: 'Facturador' },
  { path: '/bodegas', title: 'Bodegas' },
  { path: '/entregas-sap', title: 'Entregas SAP' },
  { path: '/webhooks-salientes', title: 'Webhooks salientes' },
  { path: '/simulacion/picking', title: 'Simulación de picking' },
  { path: '/procesos', title: 'Procesos' },
  { path: '/referencia/webhooks-salientes', title: 'Guía de webhooks salientes' },
];

/**
 * Si el menu ofrece la simulacion. Las tres condiciones, y falla cerrado:
 *
 * - la API dice que esta encendida (`SIMULATION_ENABLED`);
 * - la API dice que su ambiente no es produccion: lo decide la API
 *   (`DEPLOYMENT_TIER`, sin la variable `NODE_ENV=production` es produccion) y
 *   no la etiqueta del monitor, porque el servidor de desarrollo se llama
 *   `desarrollo` y si la tiene;
 * - y el monitor no se compilo para produccion.
 *
 * Sin respuesta (cargando, o la consulta fallo), no se ofrece.
 */
function simulationOffered(
  capabilities: SimulationCapabilities | null,
  environmentKind: EnvironmentKind,
): boolean {
  return (
    capabilities !== null &&
    capabilities.enabled &&
    capabilities.deploymentTier !== 'production' &&
    environmentKind !== 'production'
  );
}

function isInSection(pathname: string, sections: readonly string[]): boolean {
  return sections.some((section) => pathname === section || pathname.startsWith(`${section}/`));
}

/**
 * Titulo de la pestana para una ruta. Una ruta que no esta en la tabla toma el
 * nombre de su seccion del menu; si tampoco tiene, lo decide la pantalla (la de
 * "no existe" pone el suyo).
 */
function pageTitleFor(pathname: string): string | null {
  const page = PAGE_TITLES.find((candidate) => matchPath(candidate.path, pathname) !== null);
  if (page !== undefined) {
    return page.title;
  }
  for (const group of NAV_GROUPS) {
    const item = group.items.find((candidate) => isInSection(pathname, candidate.sections));
    if (item !== undefined) {
      return item.label;
    }
  }
  return null;
}

export function AppShell(): ReactElement {
  const environment = readEnvironment();
  const environmentKind = classifyEnvironment(environment.environmentLabel);
  const api = useMonitorApi();
  // Una sola vez por sesion del marco: si falla, la seccion queda escondida.
  const simulation = useAsyncResource(() => api.getSimulationCapabilities(), [api]);
  const showSimulation = simulationOffered(simulation.data, environmentKind);
  const navGroups = NAV_GROUPS.filter(
    (group) => group.requiresSimulation !== true || showSimulation,
  );
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => readLocalPreference(SIDEBAR_STORAGE_KEY) === 'collapsed',
  );
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  useDocumentTitle(pageTitleFor(location.pathname));

  // Cualquier navegacion cierra el panel movil (tambien el "atras" del navegador):
  // el panel tapa justo la pantalla a la que se acaba de ir.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!navOpen) {
      return undefined;
    }
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  const closeNav = (): void => {
    setNavOpen(false);
    menuButtonRef.current?.focus();
  };

  /** Al elegir destino desde el panel movil, el foco sigue al contenido. */
  const onNavigate = (): void => {
    if (navOpen) {
      setNavOpen(false);
      mainRef.current?.focus({ preventScroll: true });
    }
  };

  const toggleCollapsed = (): void => {
    const next = !collapsed;
    setCollapsed(next);
    writeLocalPreference(SIDEBAR_STORAGE_KEY, next ? 'collapsed' : 'expanded');
  };

  const skipToContent = (event: MouseEvent<HTMLAnchorElement>): void => {
    // Sin esto el ancla deja `#contenido` en la URL y una entrada en el historial.
    event.preventDefault();
    mainRef.current?.focus();
  };

  const shellClass = ['app', collapsed ? 'app--rail' : '', navOpen ? 'app--nav-open' : '']
    .filter((name) => name !== '')
    .join(' ');

  return (
    <div className={shellClass} data-environment={environmentKind}>
      <a className="skip-link" href="#contenido" onClick={skipToContent}>
        Saltar al contenido
      </a>

      <header className="topbar">
        <button
          ref={menuButtonRef}
          type="button"
          className="icon-button topbar__menu"
          aria-controls="app-sidebar"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(true)}
        >
          <IconMenu size={20} />
          <span className="visually-hidden">Abrir menú</span>
        </button>
        <Link to="/tablero" className="topbar__brand">
          <MiddleMark size={24} />
          <span>Middle</span>
        </Link>
        <EnvironmentBadge label={environment.environmentLabel} className="topbar__env" />
      </header>

      <div id="app-sidebar" className="sidebar">
        <div className="sidebar__header">
          <Link to="/tablero" className="sidebar__brand" onClick={onNavigate}>
            <MiddleMark size={30} className="sidebar__mark" />
            <span className="sidebar__brand-text">
              <span className="sidebar__name">Middle</span>
              <span className="sidebar__tagline">Monitor de integraciones</span>
            </span>
          </Link>
          <button type="button" className="icon-button sidebar__toggle" onClick={toggleCollapsed}>
            {collapsed ? <IconSidebarExpand /> : <IconSidebarCollapse />}
            <span className="tip">{collapsed ? 'Expandir menú' : 'Contraer menú'}</span>
          </button>
          <button
            ref={closeButtonRef}
            type="button"
            className="icon-button sidebar__close"
            onClick={closeNav}
          >
            <IconClose size={20} />
            <span className="visually-hidden">Cerrar menú</span>
          </button>
        </div>

        <div className="sidebar__env">
          <EnvironmentBadge label={environment.environmentLabel} />
        </div>

        <nav className="sidebar__nav" aria-label="Secciones">
          {navGroups.map((group) => (
            <div className="navgroup" key={group.id}>
              <span className="navgroup__title" id={`menu-${group.id}`}>
                {group.title}
              </span>
              <ul className="navgroup__list" aria-labelledby={`menu-${group.id}`}>
                {group.items.map((item) => {
                  const active = isInSection(location.pathname, item.sections);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className={active ? 'navlink navlink--active' : 'navlink'}
                        aria-current={active ? 'page' : undefined}
                        onClick={onNavigate}
                      >
                        <item.icon className="navlink__icon" />
                        <span className="navlink__label">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar__footer">
          <ThemeSwitcher className="sidebar__theme" />
          <ThemeCycleButton className="sidebar__theme-cycle" />
          {user === null ? null : <UserPanel user={user} onSignOut={signOut} />}
        </div>
      </div>

      {navOpen ? <div className="app__backdrop" aria-hidden="true" onClick={closeNav} /> : null}

      <main ref={mainRef} id="contenido" className="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

/** Quien esta conectado y con que rol: el rol decide que acciones se ofrecen. */
function UserPanel({
  user,
  onSignOut,
}: {
  readonly user: SessionUser;
  readonly onSignOut: () => void;
}): ReactElement {
  return (
    <div className="sidebar__user">
      <UserAvatar user={user} />
      <div className="sidebar__identity">
        <span className="sidebar__email" title={user.email}>
          {user.email}
        </span>
        <span className="sidebar__role">{ROLE_LABELS[user.role]}</span>
      </div>
      <button type="button" className="icon-button sidebar__logout" onClick={onSignOut}>
        <IconLogout size={16} />
        <span className="sidebar__logout-label">Salir</span>
      </button>
    </div>
  );
}

/** Foto de Google si hay (y carga); si no, las iniciales del nombre. */
function UserAvatar({ user }: { readonly user: SessionUser }): ReactElement {
  const [broken, setBroken] = useState(false);

  if (user.picture !== null && !broken) {
    return (
      <img
        className="avatar"
        src={user.picture}
        alt=""
        title={user.email}
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="avatar avatar--initials" aria-hidden="true" title={user.email}>
      {initialsOf(user)}
    </span>
  );
}

function initialsOf(user: SessionUser): string {
  const words = user.name
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  const letters =
    words.length > 0 ? words.slice(0, 2).map((word) => word.charAt(0)) : [user.email.charAt(0)];
  return letters.join('').toUpperCase();
}
